import { AriaCoreDatapoint, VerbosePerfEventType } from "../datapoints/AriaCoreDatapoint";
import type { AriaDatapointType, CustomDataMap, DataPointEventType, InternalDatapointOptions, CoreAnalyticsOptions, TraceErrorObject, LogDatapointObservation, LogDatapointObserver, } from "owa-analytics-types";
import { getInitializedAnalytics } from "../initializeAnalytics";
import optionallyTraceDatapoint from "../utils/optionallyTraceDatapoint";
import { validatePropertyBag } from "../utils/validatePropertyBag";
import isDevOrAutomationEnvironment from "../utils/isDevOrAutomationEnvironment";
import getFlightControl from "../utils/getFlightControl";
import getSampledRate from "../utils/getSampledRate";
import shouldLogDatapoint from "../utils/shouldLogDatapoint";
import { createEvent, logOneDSDatapoint } from "../OneDsWrapper";
import { isRunningOnWorker } from "owa-config/lib/isRunningOnWorker";
import isQosDatapoint from "../utils/isQosDatapoint";
import { ValueKind } from "@microsoft/1ds-analytics-js";
import type { IEventProperty } from "@microsoft/1ds-analytics-js";
import { logUnifiedEvents } from "./logUnifiedDatapoint";
import { errorThatWillCauseAlertAndThrow } from "owa-trace";
import { highCountEventThrottle } from "../utils/highCountEventThrottle";
import { getAnalyticsFlightsAndAppSettings } from "../settings/getAnalyticsFlightsAndAppSettings";
import { getAriaTenantToken } from "owa-analytics-shared/lib/settings/getAriaTenantToken";
import type { AccountKey } from "owa-client-types";
import { mapToGlobalAccountKey } from "../utils/mapToGlobalAccountKey";
import { enrichDatapointWithAppVisibility } from "../utils/enrichDatapointWithAppVisibility";
import { getLogDatapointObserver } from "owa-analytics-utils/lib/logDatapointObservation";
import { partitionEcsConfigIds } from "owa-analytics-utils";
import { getCachedSettings } from "../settings/getCachedSettings";

const MAX_MISSING_ANALYTICS_OPTIONS_ERRORS = 25;
let missingAnalyticsOptionsCount = 0;

export async function logDatapoint(datapoint: AriaDatapointType, overrideEventType?: DataPointEventType, batchId?: number, observer?: LogDatapointObserver): Promise<void> {
    if (isRunningOnWorker()) {
        datapoint = AriaCoreDatapoint.fromJSObject<AriaDatapointType>(datapoint);
    }
    
    enrichDatapointWithAppVisibility(datapoint, false);
    
    const properties = datapoint.getAllProperties() ?? {};
    if (observer) {
        if (!observer.isActive()) {
            return;
        }
        const mapping = observer.observation.mapping;
        const configIds = partitionEcsConfigIds(mapping.configIds);
        if (datapoint.getAccountKey() !== mapping.accountKey ||
            datapoint.getCreationTime() !== observer.observation.createdAt ||
            properties.EventName !== "EcsConfigStateStamping" ||
            properties.EcsETag !== mapping.etag ||
            properties.EcsConfigIds !== configIds.EcsConfigIds ||
            properties.EcsDefaultConfigIds !== configIds.EcsDefaultConfigIds) {
            observer.settle("unsupported");
            return;
        }
    }
    
    const datapointOptions = (datapoint.getOptions() ?? {}) as InternalDatapointOptions;
    const analyticsOptions = getInitializedAnalytics();
    
    if (!analyticsOptions) {
        observer?.settle("failed");
        // Only log it once per session
        const isNotLogDatapointError = !(properties?.message as string | undefined)?.includes?.("LogDatapointCalledBeforeInitialization");
        
        // We could get an infinite loop if we throw an errors here, so we limit the number of errors we throw.
        if (missingAnalyticsOptionsCount++ < MAX_MISSING_ANALYTICS_OPTIONS_ERRORS &&
            isNotLogDatapointError) {
            const traceError: TraceErrorObject = new Error("Analytics_Addons_LogDatapointCalledBeforeInitialization");
            
            const upstreamMessage = properties?.message as string | undefined;
            const upstreamStack = properties?.stack as string | undefined;
            
            traceError.additionalInfo = {
                isRunningOnWorker: isRunningOnWorker(),
                analyticsOptionsType: typeof analyticsOptions,
                eventName: datapoint.getEventName(),
                ...(upstreamMessage ? { upstreamMessage } : {}),
            };
            
            if (upstreamStack) {
                traceError.stack = upstreamStack;
            }
            
            errorThatWillCauseAlertAndThrow(traceError);
        }
        return;
    }
    
    const datapointAccountKey = datapoint.getAccountKey();
    // Make sure we fallback to the 'globalAccount' if we get a "default_emptyAccountKey" or undefined.
    const accountKey: AccountKey | "globalAccount" = mapToGlobalAccountKey(datapointAccountKey);
    if ((properties.EventName === "MailComposeSend" ||
        properties.EventName === "SwitchMailFolder") &&
        getAnalyticsFlightsAndAppSettings(accountKey)?.disableEcsPairedStagesStamping !== true) {
        const ecsPairedStages = getCachedSettings(accountKey)?.ecsConfigState?.rolloutConfig?.EcsPairedStages;
        if (ecsPairedStages) {
            properties.EcsPairedStages = ecsPairedStages;
        }
    }
    const eventType = getEventType(datapoint, overrideEventType);
    const flightControl = getFlightControl(datapoint, analyticsOptions, eventType, accountKey);
    
    // While gathering data, we do not throttle the events
    if (getAnalyticsFlightsAndAppSettings(accountKey)?.enableHighCountEventThrottle) {
        highCountEventThrottle(datapoint, getAnalyticsFlightsAndAppSettings(accountKey)?.highCountEventThrottleWindow, getAnalyticsFlightsAndAppSettings(accountKey)?.highCountEventThrottleThreshold);
    }
    
    const isFullVerbose = getAnalyticsFlightsAndAppSettings(accountKey)?.isFullVerboseLoggingEnabled;
    const shouldLogInOWTenant = await shouldLogDatapoint(datapoint, analyticsOptions, eventType, accountKey);
    if (observer && !observer.isActive()) {
        return;
    }
    if (datapointOptions.unifiedTelemetryOnly || (!shouldLogInOWTenant && !isFullVerbose)) {
        observer?.settle("suppressed");
        // Either caller forced UnifiedTelemetry‐only, or we decided not to log in the OW tenant —
        // in both cases, just mirror into Unified Telemetry and exit.
        await logUnifiedEvents(datapoint, analyticsOptions, eventType, accountKey);
        return;
    }
    
    properties.EventType = "standard";
    if (isQosDatapoint(datapoint)) {
        properties.EventType = "qos";
        const { throughAFD, serviceVersion, dag } = analyticsOptions.startupInfo;
        properties.ThroughAFD = throughAFD;
        properties.ServiceVersion = serviceVersion;
        properties.Dag = dag;
    }
    
    if (getAnalyticsFlightsAndAppSettings(accountKey)?.isFullVerboseLoggingEnabled) {
        properties["OriginalEvent"] = eventType;
    }
    
    properties["Sampled"] = getSampledRate(flightControl?.rate, datapointOptions?.sessionSampleRate);
    
    if (batchId) {
        properties["BatchId"] = batchId;
    }
    
    const propertyBag = datapoint.getPropertyBag();
    if (propertyBag) {
        properties["MiscData"] = validatePropertyBag(propertyBag, datapoint.getEventName(), datapoint.getOptions(), !!getAnalyticsFlightsAndAppSettings(accountKey)?.turnOffCustomData);
    }
    
    if (datapointOptions.isScorecardEvent) {
        properties["IsScorecardEvent"] = true;
    }
    
    optionallyTraceDatapoint(datapoint, eventType, accountKey, analyticsOptions);
    
    // let's not actually log datapoints if we are gulping or in a branch or a tds box
    // unless the datapoint is explicitly whitelisted
    if (!isDevOrAutomationEnvironment() || eventType == "client_event_dev_only") {
        const token = getAriaTenantToken(<string | undefined>datapoint.getData("App") ?? "Mail", datapointOptions?.tenantOverride);
        if (token) {
            await logEvent(token, datapoint, properties, eventType, analyticsOptions, undefined, accountKey, observer);
        }
        else {
            observer?.settle("suppressed");
        }
        
        // TODO(scorecard): Dual-upload scorecard-flagged events to client_scorecard_event.
        // When ready:
        //   1. Generate a shared eventId (crypto.randomUUID()) before the primary logEvent call
        //      and attach it to the client_event properties so both tables can be correlated.
        //   2. Retrieve CommonProperties via getCommonProperties(analyticsOptions).
        //   3. const record = buildScorecardEventRecord(datapoint, commonProperties, eventId);
        //   4. if (record) { /* send record to client_scorecard_event */ }
        //
        // if (datapointOptions?.isScorecardEvent) { ... }
    }
    else {
        observer?.settle("suppressed");
    }
    
    // Log as Unified Event if datapoint matches ECS catalogs
    await logUnifiedEvents(datapoint, analyticsOptions, eventType, accountKey);
}

async function logEvent(tenantToken: string, datapoint: AriaDatapointType, properties: CustomDataMap, eventType: DataPointEventType, analyticsOptions: CoreAnalyticsOptions, updateAppSettings?: boolean, accountKey: AccountKey | "globalAccount" = "globalAccount", observer?: LogDatapointObserver) {
    const e = createEvent(getAnalyticsFlightsAndAppSettings(accountKey)?.isFullVerboseLoggingEnabled
        ? VerbosePerfEventType
        : eventType, properties, datapoint.getCreationTime());
    
    if (properties["E2ETimeElapsed"]) {
        e.latency = <number>properties["E2ETimeElapsed"];
    }
    
    const piiData = datapoint.getPiiData?.();
    if (e.data && piiData) {
        e.data["PiiData"] = <IEventProperty>{
            value: piiData,
            kind: ValueKind.Pii_Identity,
        };
    }
    
    const customDiagnosticsOptions: CustomDataMap = (datapoint.getOptions() as InternalDatapointOptions)?.customDiagnosticsOptions ?? {};
    
    // schema-e2e: table-guard injection point — do not remove
    //e.name = 'junk_test_table'; // TEST - comment before commit
    
    await logOneDSDatapoint({
        tenantToken,
        item: e,
        accountKey,
        analyticsOptions,
        updateAppSettings,
        customDiagnosticsOptions,
        observer,
    });
}

export async function logDatapointWithObservation(datapoint: AriaDatapointType, overrideEventType: DataPointEventType | undefined, observation: LogDatapointObservation, batchId?: number): Promise<void> {
    const observer = getLogDatapointObserver(observation);
    if (!observer?.claimDispatch()) {
        return;
    }
    try {
        // The ordinary core API's third argument remains the worker batch ID.
        await logDatapoint(datapoint, overrideEventType, batchId, observer);
    }
    catch (error) {
        observer.settle("failed");
        throw error;
    }
}

function getEventType(datapoint: AriaDatapointType, overrideEventType: DataPointEventType | undefined): DataPointEventType {
    if (overrideEventType) {
        return overrideEventType;
    }
    
    const datapointOptions = datapoint.getOptions() as InternalDatapointOptions;
    
    if (datapointOptions?.isGreyError) {
        return "client_grey_error";
    }
    
    if (datapointOptions?.isVerbose) {
        return "client_verbose";
    }
    
    if (datapointOptions?.excludeFromKusto) {
        return "client_cosmos";
    }
    
    return "client_event";
}
